import { c as _c } from "react/compiler-runtime";
import posthog from "posthog-js";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import { useCallback, useEffect, useMemo } from "react";
import { usePathname } from "next/navigation";
import { useProgressNavigation } from "@/components/progress-bar";
import { queryClient } from "@/config/react-query";
import { useUser } from "@/contexts/user-provider";
import { type ScribeTranscriptionDeps, startRecordingWithTranscription as startRecordingWithTranscriptionFn } from "@/hooks/use-scribe-transcription";
import { useRecordingNote } from "@/hooks/use-selected-note";
import { getSupportedMimeType } from "@/lib/audio";
import { clinicClock } from "@/lib/clinic-clock";
import { type MicErrorKind, classifyMicError } from "@/lib/microphone-error";
import { parseError } from "@/lib/parse-error";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { logTimestamp } from "@/lib/transcription-queue";
import { toast } from "@/lib/use-toast";
import { CreateNoteState, NoteType } from "@/types/enums/note-state";
import { type NoteDetail, type NoteListItem, notePatientToPatientSummary } from "@/types/notes";
import { Page } from "@/types/pagination";
import { PatientDetail } from "@/types/patient";
import { ProviderSummary } from "@/types/provider";
import { useTenantStore } from "./tenant-store";
import { useTranscriptionQueue } from "./transcription-queue-store";
import { noteQueryKeys, noteQueryOptions } from "@/api/provider/scribe/note-query";
import { getNotes } from "@/api/provider/scribe/notes";
import { pauseTelehealthVisit, resumeTelehealthVisit } from "@/api/provider/telehealth";

// Local type from removed provider
// Note date types removed; server state now comes from React Query

type DetectionsMap = Map<string, any>; // keep generic to avoid wide type imports

type IntervalId = ReturnType<typeof setInterval> | null;
const LOG_PREFIX = () => `[${logTimestamp()}][ScribeStore]`;

/** Auto-pause recording when no new transcript content for this long. */
const SILENCE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

/** Warn user when no transcript growth for this long. */
const NO_TRANSCRIPT_WARNING_MS = 90 * 1000; // 90 seconds

interface StartOptions {
  deviceId?: string;
  mimeType?: string;
  chunkMs?: number;
  resetTimer?: boolean;
  // Recording context for queue-based transcription
  providerId?: string;
  locationId?: string;
  patientId?: string;
  // A stream from acquireMic to record from, instead of opening the mic again.
  stream?: MediaStream;
}
interface ScribeDeviceSlice {
  selectedDevice?: string;
  audioDevices: {
    deviceId: string;
    label: string;
  }[];
  micPermissionGranted: boolean;
  // Set when the browser refused the microphone; opens MicAccessDialog.
  micErrorKind: MicErrorKind | null;
  setSelectedDevice: (deviceId: string) => void;
  setMicPermissionGranted: (granted: boolean) => void;
  setMicErrorKind: (kind: MicErrorKind | null) => void;
  setAudioDevices: (devices: {
    deviceId: string;
    label: string;
  }[]) => void;
}

/**
 * Where the audio for the currently-tracked recording is coming from. The mic
 * flow drives MediaRecorder locally and POSTs chunks to the `/transcribe`
 * route; the telehealth flow gets audio from the Recall bot (pause/resume/stop
 * are HTTP calls) and its transcript text lands server-side via the Recall
 * webhook, not the mic route.
 */
export type AudioSource = "mic" | "telehealth";
interface ScribeRecordingSlice {
  audioStream: MediaStream | null;
  mediaRecorder: MediaRecorder | null;
  isProcessing: boolean;
  error?: string;
  chunkIntervalId: IntervalId;
  secondsTimerId: IntervalId;
  lastPendingPromise: Promise<string | undefined> | null;
  recordedChunks: Blob[] | null;
  lastChunkMs?: number;
  lastMimeType?: string;
  // Recording-scoped identifiers (decoupled from viewing state)
  currentRecordingId?: string;
  audioSource?: AudioSource;
  // The Recall bot whose telehealth Note we're waiting on the webhook to
  // publish (via TELEHEALTH_BOT_STATUS) so the global PIP can switch to it.
  // Set on a Join click whose bot has no Note yet — a fresh visit, or a
  // rejoin after the prior bot ended — and cleared once that bot's alert
  // lands and hydrates the recording slice. We key on `recallBotId`, not the
  // event: one event can accrue several bots/Notes (rejoins), so the event
  // alone can't tell which Note is the one we just spawned. Store-backed (not
  // component-local) so the hydration survives the Join click's navigation.
  pendingTelehealthHydration?: {
    eventId: string;
    recallBotId: string;
  };
  // Recording context (set when starting, used by queue)
  recordingProviderId?: string;
  recordingLocationId?: string;
  recordingPatientId?: string;
  // Recording-specific UI state (decoupled from viewing)
  setRecordedMoreThan5Seconds: (s: boolean) => void;
  recordedMoreThan5Seconds: boolean;
  recordingNoteState: CreateNoteState;
  // Silence auto-pause: track when the transcript last grew
  lastTranscriptGrowthAt: number;
  prevTranscriptLength: number;
  noTranscriptWarningShown: boolean;
  // Transcription state
  isTranscribing: boolean;
  // Note id whose audio-chunk upload last failed, or null. Scoped to a note
  // (not a global flag) so the "Error saving audio" badge only shows on the
  // note that actually errored, clears when a later chunk succeeds, and never
  // leaks onto an unrelated note (e.g. a telehealth note, which has no
  // client-side chunk upload at all).
  transcriptionErrorNoteId: string | null;
  isStartingRecording: boolean;
  // True while a mic `startAudio` is between its isActive check and storing
  // its recorder. Set before the first await so an overlapping start (e.g. a
  // double-fired resume) returns instead of opening a second recorder whose
  // intervals `pauseAudio` could never clear.
  isStartingAudio: boolean;
  setIsTranscribing: (v: boolean) => void;
  setTranscriptionErrorNoteId: (noteId: string | null) => void;
  setIsStartingRecording: (v: boolean) => void;
  startAudio: (options: StartOptions) => Promise<void>;
  acquireMic: () => Promise<MediaStream>;
  checkMicAccess: () => Promise<void>;
  pauseAudio: () => void;
  stopAudio: (currentUser: ProviderSummary | null | undefined) => void;
  onScribePage: boolean;
  isActive: boolean;
  switchAudioDevice: (deviceId: string, opts?: {
    mimeType?: string;
    chunkMs?: number;
  }) => Promise<void>;
  setAudioDevice: (deviceId: string) => Promise<void>;
  setRecordingId: (id?: string) => void;
  /** The local draft became its server note: same recording, new id. */
  promoteDraftRecording: (id: string) => void;
  setPendingTelehealthHydration: (pending?: {
    eventId: string;
    recallBotId: string;
  }) => void;
  setAudioSource: (source?: AudioSource) => void;
  setRecordingNoteState: (s: CreateNoteState) => void;
  setVisiblePatientId: (id?: string) => void;
  visiblePatientId?: string;
  setRecordingSeconds: (seconds: number) => void;
}

/**
 * Minimal patient shape `handlePlayPause` needs to gate AI scribe recording.
 * Kept narrow so callers can pass `Patient`, `PatientSummary`, the patient
 * embedded in a `Note`, etc. without coupling to a specific schema.
 */
export interface HandlePlayPausePatient {
  id: string;
  aiScribeConsent?: boolean | null;
}
interface UseScribeRecording extends Pick<ScribeRecordingSlice, "recordingNoteState" | "currentRecordingId" | "audioSource" | "onScribePage" | "isActive" | "pauseAudio" | "stopAudio" | "startAudio" | "audioStream" | "isProcessing" | "recordedMoreThan5Seconds" | "setRecordingId" | "setAudioSource" | "visiblePatientId" | "setVisiblePatientId" | "isTranscribing" | "transcriptionErrorNoteId" | "isStartingRecording" | "setIsTranscribing" | "setTranscriptionErrorNoteId" | "setIsStartingRecording" | "setRecordingSeconds" | "setRecordingNoteState"> {
  /** Resolves true only when a recording actually started or resumed. */
  handlePlayPause: (patient?: HandlePlayPausePatient, overrideOptOut?: boolean) => Promise<boolean>;
  flushTranscription: () => Promise<string | undefined>;
  handleResumeRecording: (note: NoteListItem) => void;
  userHasAiScribeUsePermission: boolean;
}
interface ScribeNoteSlice {
  previouslySelectedNoteType: NoteType;
  fillFromLastNote: boolean;
  defaultNoteTemplateId: string | null;
  linkedEventId: string | null;
  /** Template the in-flight recording should generate against. */
  recordingNoteTemplateId: string | null;
  /**
   * Templates to list first when the note recorded from an appointment has
   * none: its services' templates when several differ. Kept per note id,
   * so they outlast the recording and never label another note.
   */
  recordingSuggestedNoteTemplates: Record<string, string[]>;
  noteState: CreateNoteState;
  detections?: DetectionsMap;
  setPreviouslySelectedNoteType: (t: NoteType) => void;
  setFillFromLastNote: (fill: boolean) => void;
  setDefaultNoteTemplateId: (id: string | null) => void;
  setLinkedEventId: (id: string | null) => void;
  setRecordingNoteTemplateId: (id: string | null) => void;
  setNoteState: (s: CreateNoteState) => void;
  handleNewNote: (currentPatient: PatientDetail, currentUser: ProviderSummary, noteType: NoteType, linkedEventId?: string | null, noteTemplateId?: string | null, suggestedNoteTemplateIds?: string[]) => NoteDetail;
  setDetections: (updater: (prev: DetectionsMap | undefined) => DetectionsMap | undefined) => void;
}
interface ScribeSessionSlice {
  isGenerateRequested: boolean;
  requestGenerateNote: () => void;
  clearGenerateRequest: () => void;
}
interface ScribeTimeSlice {
  recordingSeconds: number;
}
type ScribeStore = ScribeDeviceSlice & ScribeRecordingSlice & ScribeNoteSlice & ScribeSessionSlice & ScribeTimeSlice;

/** Logs a refused `getUserMedia` and returns the state that opens
 *  MicAccessDialog. */
function reportMicError(e: any) {
  console.error(`${LOG_PREFIX()} Microphone access error:`, e);
  const micErrorKind = classifyMicError(e);
  posthog.capture("scribe_mic_error", {
    error: e?.message ?? "Unable to access microphone",
    error_name: e?.name,
    mic_error_kind: micErrorKind
  });
  return {
    micErrorKind,
    error: e?.message ?? "Unable to access microphone"
  };
}

/** Opens the mic recording will use: the remembered one, or the default if
 *  that one was unplugged or renamed since it was picked. Returns the id of
 *  the device actually opened. */
async function openMic(deviceId?: string): Promise<{
  stream: MediaStream;
  deviceId?: string;
}> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: deviceId ? {
        deviceId: {
          exact: deviceId
        }
      } : true
    });
    return {
      stream,
      deviceId
    };
  } catch (e) {
    if (!deviceId || (e as {
      name?: string;
    })?.name !== "OverconstrainedError") {
      throw e;
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true
    });
    return {
      stream,
      deviceId: stream.getAudioTracks()[0]?.getSettings().deviceId
    };
  }
}
export const useScribeStore = create<ScribeStore>()(persist((set, get) => ({
  // device slice
  selectedDevice: undefined,
  audioDevices: [],
  micPermissionGranted: false,
  micErrorKind: null,
  setSelectedDevice: deviceId => set({
    selectedDevice: deviceId
  }),
  setMicPermissionGranted: granted => set({
    micPermissionGranted: granted
  }),
  setMicErrorKind: kind => set({
    micErrorKind: kind
  }),
  setAudioDevices: devices => set({
    audioDevices: devices
  }),
  audioStream: null,
  mediaRecorder: null,
  isActive: false,
  isProcessing: false,
  // silence auto-pause
  lastTranscriptGrowthAt: Date.now(),
  prevTranscriptLength: 0,
  noTranscriptWarningShown: false,
  // transcription state
  isTranscribing: false,
  transcriptionErrorNoteId: null,
  isStartingRecording: false,
  isStartingAudio: false,
  error: undefined,
  chunkIntervalId: null,
  secondsTimerId: null,
  recordingSeconds: 0,
  lastPendingPromise: null,
  recordedChunks: [],
  recordedMoreThan5Seconds: false,
  lastMimeType: undefined,
  currentRecordingId: undefined,
  audioSource: undefined,
  pendingTelehealthHydration: undefined,
  recordingNoteState: CreateNoteState.IDLE,
  recordingProviderId: undefined,
  recordingLocationId: undefined,
  recordingPatientId: undefined,
  setRecordedMoreThan5Seconds: s => set({
    recordedMoreThan5Seconds: s
  }),
  startAudio: async ({
    deviceId,
    mimeType,
    chunkMs = 30000,
    resetTimer = true,
    providerId,
    locationId,
    patientId,
    stream: heldStream
  }: StartOptions) => {
    const state = get();
    if (state.audioSource === "telehealth" || state.isActive || state.isStartingAudio) {
      heldStream?.getTracks().forEach(t => t.stop());
    }
    if (state.audioSource === "telehealth") {
      // Telehealth visits are already running on the Recall side. The
      // only meaningful "start" from the UI is resuming after a pause.
      const noteId = state.currentRecordingId;
      if (!noteId) return;
      const prevState = state.recordingNoteState;
      set({
        recordingNoteState: CreateNoteState.RECORDING
      });
      try {
        await resumeTelehealthVisit(noteId);
      } catch (e) {
        set({
          recordingNoteState: prevState
        });
        console.error(`${LOG_PREFIX()} Telehealth resume failed:`, e);
        toast({
          title: "Failed to resume visit",
          description: "Could not resume the telehealth recording.",
          variant: "error"
        });
        throw e;
      }
      return;
    }
    if (state.isActive || state.isStartingAudio) return;
    set({
      isStartingAudio: true
    });
    if (state.audioStream) {
      state.audioStream.getTracks().forEach(t => t.stop());
    }
    try {
      const requestedDevice = deviceId ?? state.selectedDevice;
      if (resetTimer) {
        set({
          recordingSeconds: 0,
          lastPendingPromise: null,
          prevTranscriptLength: 0
        });
      }
      // Always reset silence window when starting/resuming
      set({
        lastTranscriptGrowthAt: Date.now(),
        noTranscriptWarningShown: false
      });
      // Store recording context for queue-based transcription
      if (providerId) set({
        recordingProviderId: providerId
      });
      if (locationId) set({
        recordingLocationId: locationId
      });
      if (patientId) set({
        recordingPatientId: patientId
      });
      // The held stream was opened before the note was created; if its mic
      // dropped meanwhile, open one afresh. Its device id comes from the
      // track, since the selection may have changed during the wait.
      const heldTrack = heldStream?.getAudioTracks()[0];
      if (heldStream && heldTrack?.readyState !== "live") {
        heldStream.getTracks().forEach(t => t.stop());
      }
      const {
        stream,
        deviceId: deviceToUse
      } = heldStream && heldTrack?.readyState === "live" ? {
        stream: heldStream,
        deviceId: heldTrack.getSettings().deviceId ?? get().selectedDevice
      } : await openMic(requestedDevice);
      const recorder = new MediaRecorder(stream, mimeType ? {
        mimeType
      } : undefined);
      const stoppedRecordingContexts: {
        noteId?: string;
        providerId?: string;
        locationId?: string;
        patientId?: string;
        mimeType?: string;
      }[] = [];
      const nativeStop = recorder.stop.bind(recorder);
      recorder.stop = () => {
        const stopState = get();
        const context = {
          noteId: stopState.currentRecordingId,
          providerId: stopState.recordingProviderId,
          locationId: stopState.recordingLocationId,
          patientId: stopState.recordingPatientId,
          mimeType: stopState.lastMimeType ?? mimeType
        };
        nativeStop();
        stoppedRecordingContexts.push(context);
      };

      // Handle MediaRecorder errors - can cause silent recording failures
      recorder.onerror = event => {
        const error = "error" in event ? (event as {
          error?: DOMException;
        }).error : null;
        console.error(`${LOG_PREFIX()} MediaRecorder error:`, error);
        posthog.capture("scribe_recorder_error", {
          error: error?.toString?.() || "Unknown error",
          recorder_state: recorder.state,
          stream_active: stream.active,
          note_id: get().currentRecordingId
        });
      };

      // Handle audio track ending - detects mic disconnect, Bluetooth sleep, etc.
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onended = () => {
          console.error(`${LOG_PREFIX()} Audio track ended: ${audioTrack.label}, readyState=${audioTrack.readyState}`);
          posthog.capture("scribe_track_ended", {
            track_label: audioTrack.label,
            track_ready_state: audioTrack.readyState,
            recorder_state: recorder.state,
            note_id: get().currentRecordingId
          });
          // Stop recording immediately when track ends
          get().pauseAudio();
        };
      }
      recorder.ondataavailable = (event: BlobEvent) => {
        if (!event.data || event.data.size === 0) {
          // Empty chunk could indicate recording issues
          console.warn(`${LOG_PREFIX()} Received empty audio chunk`);
          posthog.capture("scribe_empty_chunk", {
            has_data: !!event.data,
            data_size: event.data?.size ?? 0,
            recorder_state: recorder.state,
            stream_active: stream.active,
            note_id: get().currentRecordingId
          });
          return;
        }
        set({
          recordedChunks: [...(get().recordedChunks ?? []), event.data]
        });
      };
      recorder.onstop = () => {
        console.log(`${LOG_PREFIX()} recorder.onstop fired`);
        set({
          isProcessing: true
        });
        const stoppedContext = stoppedRecordingContexts.shift();
        // Atomically capture and clear recordedChunks to prevent race conditions
        // when multiple onstop handlers fire in quick succession
        let chunksToProcess: Blob[] = [];
        set(state => {
          const currentChunks = state.recordedChunks;
          if (!currentChunks || currentChunks.length === 0) {
            return state; // No-op if no chunks
          }
          // Capture chunks for this handler and clear the array atomically
          chunksToProcess = [...currentChunks];
          return {
            ...state,
            recordedChunks: []
          };
        });
        if (chunksToProcess.length === 0) {
          console.log(`${LOG_PREFIX()} No chunks to process, skipping`);
          set({
            isProcessing: false
          });
          return;
        }
        const currentState = get();
        const blob = new Blob(chunksToProcess, {
          type: stoppedContext?.mimeType ?? currentState.lastMimeType
        });
        console.log(`${LOG_PREFIX()} Captured ${chunksToProcess.length} chunks, total size=${blob.size} bytes`);

        // Get note ID for queue
        const noteId = stoppedContext?.noteId ?? currentState.currentRecordingId;
        if (!noteId) {
          console.warn(`${LOG_PREFIX()} Missing noteId for enqueue, skipping`);
          set({
            isProcessing: false
          });
          return;
        }

        // Enqueue to the persistent queue
        // The queue handles retries, persistence, and error isolation
        // Backend looks up transcript by note_id - no need to track transcript ID on frontend
        const queue = useTranscriptionQueue.getState();
        console.log(`${LOG_PREFIX()} Enqueueing chunk: noteId=${noteId}, size=${blob.size}`);
        void queue.enqueue({
          noteId,
          patientId: stoppedContext?.patientId || currentState.recordingPatientId || "",
          providerId: stoppedContext?.providerId || currentState.recordingProviderId || "",
          locationId: stoppedContext?.locationId ?? currentState.recordingLocationId,
          mimeType: stoppedContext?.mimeType || currentState.lastMimeType || "audio/webm",
          blob
        }).then(() => {
          console.log(`${LOG_PREFIX()} Chunk enqueued successfully`);
          set({
            isProcessing: false
          });
        }).catch(err => {
          console.error(`${LOG_PREFIX()} Failed to enqueue chunk:`, err);
          posthog.capture("transcription_enqueue_failed", {
            note_id: noteId,
            blob_size: blob.size,
            error_message: err instanceof Error ? err.message : String(err)
          });
          toast({
            title: "Recording Not Saving",
            description: "Your recording is not being saved. Please refresh the page and try again. If the issue persists, try clearing your browser cookies.",
            variant: "error",
            id: "transcription-storage-error",
            playSound: true
          });
          set({
            isProcessing: false
          });
        });
      };
      let chunkCount = 0;
      recorder.start(1000);
      const chunkIntervalId = setInterval(() => {
        chunkCount++;
        const streamActive = stream.active;
        const trackState = stream.getAudioTracks()[0]?.readyState;
        if (recorder.state === "recording") {
          // Check if stream is still healthy before stop/start cycle
          if (!streamActive || trackState !== "live") {
            console.error(`${LOG_PREFIX()} Stream unhealthy at chunk ${chunkCount}: stream.active=${streamActive}, track.readyState=${trackState}`);
            posthog.capture("scribe_stream_unhealthy", {
              chunk_count: chunkCount,
              stream_active: streamActive,
              track_state: trackState,
              recording_seconds: get().recordingSeconds,
              note_id: get().currentRecordingId
            });
            // Stream is dead - stop the recording gracefully
            get().pauseAudio();
            return;
          }
          recorder.stop();
          try {
            recorder.start(1000);
          } catch (e) {
            console.error(`${LOG_PREFIX()} Failed to restart recorder at chunk ${chunkCount}:`, e);
            posthog.capture("scribe_restart_failed", {
              chunk_count: chunkCount,
              error: e instanceof Error ? e.message : String(e),
              recorder_state: recorder.state,
              stream_active: streamActive,
              track_state: trackState,
              note_id: get().currentRecordingId
            });
            // Recorder failed to restart - clean up
            get().pauseAudio();
          }
        } else {
          // Recorder is not in "recording" state - this is unexpected during active recording
          console.error(`${LOG_PREFIX()} Unexpected recorder state at chunk ${chunkCount}: ${recorder.state}`);
          posthog.capture("scribe_unexpected_state", {
            chunk_count: chunkCount,
            recorder_state: recorder.state,
            stream_active: streamActive,
            track_state: trackState,
            is_active: get().isActive,
            note_id: get().currentRecordingId
          });
          // Clean up to prevent interval from running indefinitely
          get().pauseAudio();
        }
      }, chunkMs);
      const secondsTimerId = setInterval(() => {
        if (get().isActive) {
          set(s => ({
            recordingSeconds: s.recordingSeconds + 1
          }));
          const {
            lastTranscriptGrowthAt,
            currentRecordingId,
            recordingPatientId,
            noTranscriptWarningShown
          } = get();
          const now = Date.now();
          const silenceDuration = now - lastTranscriptGrowthAt;

          // Warn after 90s with no transcript growth
          if (!noTranscriptWarningShown && silenceDuration >= NO_TRANSCRIPT_WARNING_MS) {
            set({
              noTranscriptWarningShown: true
            });
            toast({
              title: "Audio not transcribed",
              description: "If you are still speaking, try refreshing the page.",
              variant: "warning",
              playSound: true
            });
          }

          // Auto-pause when no new transcript content for 15 minutes
          if (silenceDuration >= SILENCE_TIMEOUT_MS) {
            console.log(`${LOG_PREFIX()} Auto-pausing: no transcript growth for 15 minutes`);
            posthog.capture("scribe_silence_auto_paused", {
              note_id: currentRecordingId,
              patient_id: recordingPatientId,
              silence_duration_ms: silenceDuration
            });
            get().pauseAudio();
            toast({
              title: "Recording Paused",
              description: "No speech was detected for 15 minutes. The recording has been paused automatically.",
              variant: "info"
            });
          }
        }
      }, 1000);
      // Never drop a live recorder or interval by overwriting its handle:
      // whatever is still stored would keep recording and enqueueing
      // chunks with nothing left able to stop it.
      const prev = get();
      if (prev.chunkIntervalId) clearInterval(prev.chunkIntervalId);
      if (prev.secondsTimerId) clearInterval(prev.secondsTimerId);
      if (prev.mediaRecorder && prev.mediaRecorder !== recorder && prev.mediaRecorder.state !== "inactive") {
        try {
          prev.mediaRecorder.stop();
        } catch (e) {
          console.error("Error stopping previous recorder", e);
        }
      }
      if (prev.audioStream && prev.audioStream !== stream) {
        prev.audioStream.getTracks().forEach(t => t.stop());
      }
      set({
        audioStream: stream,
        mediaRecorder: recorder,
        isActive: true,
        error: undefined,
        chunkIntervalId,
        secondsTimerId,
        selectedDevice: deviceToUse,
        lastChunkMs: chunkMs,
        lastMimeType: mimeType,
        recordingNoteState: CreateNoteState.RECORDING
      });
    } catch (e: any) {
      heldStream?.getTracks().forEach(t => t.stop());
      set({
        ...reportMicError(e),
        isActive: false,
        isProcessing: false,
        audioStream: null,
        mediaRecorder: null
      });
      throw e;
    } finally {
      set({
        isStartingAudio: false
      });
    }
  },
  // Opens the mic recording will use, so a caller can learn the browser
  // will refuse before it creates anything server-side, then record from
  // this same stream (startAudio's `stream`) rather than open it again.
  acquireMic: async () => {
    try {
      const {
        stream,
        deviceId
      } = await openMic(get().selectedDevice);
      set({
        micPermissionGranted: true,
        micErrorKind: null,
        selectedDevice: deviceId
      });
      return stream;
    } catch (e: any) {
      set(reportMicError(e));
      throw e;
    }
  },
  checkMicAccess: async () => {
    const stream = await get().acquireMic();
    stream.getTracks().forEach(t => t.stop());
  },
  pauseAudio: () => {
    const {
      audioSource,
      currentRecordingId,
      mediaRecorder,
      audioStream,
      chunkIntervalId,
      secondsTimerId,
      recordingNoteState: prevState
    } = get();
    if (audioSource === "telehealth") {
      if (!currentRecordingId) return;
      // Optimistic local PAUSED. Recall has no distinct "paused"
      // lifecycle status — both ``bot.in_call_recording`` and
      // ``bot.in_call_not_recording`` collapse to ``listening`` — so
      // the server can't reflect this transition back through the
      // adapter. Roll back on API failure.
      //
      // ``isProcessing: true`` is the mic-parity badge signal: the
      // pause endpoint flushes the bridge buffer server-side, and
      // ``useTelehealthTranscriptStream`` clears the flag when the
      // resulting NOTE_UPDATED arrives. Same pattern as mic's
      // recorder.onstop (optimistic on, cleared by queue.enqueue's
      // resolve), different transport.
      set({
        recordingNoteState: CreateNoteState.PAUSED,
        isProcessing: true
      });
      void pauseTelehealthVisit(currentRecordingId).catch(e => {
        set({
          recordingNoteState: prevState,
          isProcessing: false
        });
        console.error(`${LOG_PREFIX()} Telehealth pause failed:`, e);
        toast({
          title: "Failed to pause visit",
          description: "Could not pause the telehealth recording.",
          variant: "error"
        });
      });
      return;
    }
    if (chunkIntervalId) clearInterval(chunkIntervalId);
    if (secondsTimerId) clearInterval(secondsTimerId);
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      try {
        mediaRecorder.stop();
      } catch (e) {
        console.error("Error stopping audio", e);
      }
    }
    if (audioStream) {
      audioStream.getTracks().forEach(t => t.stop());
    }
    set({
      chunkIntervalId: null,
      secondsTimerId: null,
      isActive: false,
      audioStream: null,
      mediaRecorder: null,
      recordingNoteState: CreateNoteState.PAUSED
    });
  },
  stopAudio: (currentUser: ProviderSummary | null | undefined) => {
    // Mic-only entry point. Telehealth recordings end when the bot
    // leaves the Meet call (Recall auto-terminate); there's no
    // user-initiated stop path in the PIP for telehealth. Guard
    // explicitly: a stray call would pauseAudio() (pausing the bot) then
    // clear currentRecordingId, orphaning the still-running visit.
    const {
      audioSource,
      currentRecordingId
    } = get();
    if (audioSource === "telehealth") return;
    console.log(`${LOG_PREFIX()} Stopping audio: noteId=${currentRecordingId}`);
    get().pauseAudio();
    posthog.capture("scribe_stopped", {
      note_id: currentRecordingId,
      patient_id: get().visiblePatientId,
      provider_id: currentUser?.id
    });
    set({
      recordingNoteState: CreateNoteState.IDLE,
      currentRecordingId: undefined,
      recordingSeconds: 0,
      prevTranscriptLength: 0
    });
  },
  switchAudioDevice: async (deviceId: string, opts?: {
    mimeType?: string;
    chunkMs?: number;
  }) => {
    console.log(`${LOG_PREFIX()} Switching audio device: ${get().selectedDevice} -> ${deviceId}`);
    posthog.capture("scribe_device_switched", {
      previous_device: get().selectedDevice,
      new_device: deviceId,
      note_id: get().currentRecordingId
    });
    const wasRecording = get().isActive;
    const {
      recordingProviderId,
      recordingLocationId,
      recordingPatientId
    } = get();
    get().pauseAudio();
    if (wasRecording) {
      await get().startAudio({
        deviceId,
        mimeType: opts?.mimeType ?? get().lastMimeType,
        chunkMs: opts?.chunkMs ?? get().lastChunkMs ?? 30000,
        resetTimer: false,
        providerId: recordingProviderId,
        locationId: recordingLocationId,
        patientId: recordingPatientId
      });
    } else {
      set({
        selectedDevice: deviceId
      });
    }
  },
  setAudioDevice: async (deviceId: string) => {
    if (get().isActive) {
      await get().switchAudioDevice(deviceId);
    } else {
      set({
        selectedDevice: deviceId
      });
    }
  },
  setRecordingId: id => set(state => {
    const next = id ?? undefined;
    if (next === state.currentRecordingId) return {};
    return {
      currentRecordingId: next,
      isProcessing: false
    };
  }),
  promoteDraftRecording: id => set(state => {
    const draftId = state.currentRecordingId;
    const suggested = draftId ? state.recordingSuggestedNoteTemplates[draftId] : undefined;
    if (!draftId || !suggested) {
      return {
        currentRecordingId: id,
        isProcessing: false
      };
    }
    // The draft's appointment templates move to its server note.
    const {
      [draftId]: _draft,
      ...others
    } = state.recordingSuggestedNoteTemplates;
    return {
      currentRecordingId: id,
      isProcessing: false,
      recordingSuggestedNoteTemplates: {
        ...others,
        [id]: suggested
      }
    };
  }),
  setPendingTelehealthHydration: pending => set({
    pendingTelehealthHydration: pending ?? undefined
  }),
  setAudioSource: source => set({
    audioSource: source ?? undefined
  }),
  setRecordingNoteState: s => set({
    recordingNoteState: s
  }),
  setIsTranscribing: v => set({
    isTranscribing: v
  }),
  setTranscriptionErrorNoteId: noteId => set({
    transcriptionErrorNoteId: noteId
  }),
  setIsStartingRecording: v => set({
    isStartingRecording: v
  }),
  previouslySelectedNoteType: NoteType.AI,
  fillFromLastNote: false,
  defaultNoteTemplateId: null,
  linkedEventId: null,
  recordingNoteTemplateId: null,
  recordingSuggestedNoteTemplates: {},
  noteState: CreateNoteState.IDLE,
  detections: undefined,
  currentViewingVersion: undefined,
  setPreviouslySelectedNoteType: t => set({
    previouslySelectedNoteType: t
  }),
  setFillFromLastNote: fill => set({
    fillFromLastNote: fill
  }),
  setDefaultNoteTemplateId: id => set({
    defaultNoteTemplateId: id
  }),
  setLinkedEventId: id => set({
    linkedEventId: id
  }),
  setRecordingNoteTemplateId: id => set({
    recordingNoteTemplateId: id
  }),
  setNoteState: s => set({
    noteState: s
  }),
  handleNewNote: (currentPatient: PatientDetail, currentUser: ProviderSummary, noteType: NoteType, linkedEventId?: string | null, noteTemplateId?: string | null, suggestedNoteTemplateIds?: string[]) => {
    console.log(`${LOG_PREFIX()} Creating new note: patientId=${currentPatient.id}, noteType=${noteType}`);
    posthog.capture("scribe_new_note_created", {
      patient_id: currentPatient.id,
      provider_id: currentUser.id,
      note_type: noteType
    });
    const {
      setRecordingId
    } = get();
    const localId = "local_id";
    const draft: NoteDetail = {
      id: localId,
      parentNoteId: localId,
      version: 1,
      currentVersion: 1,
      aiGenerated: noteType === NoteType.AI,
      createdDate: new Date().toISOString(),
      patient: currentPatient,
      providers: [currentUser],
      duration: 0,
      providerId: currentUser.id,
      sourceDocuments: []
    };

    // Reset audioSource — a previous telehealth visit may have left it
    // set to "telehealth"; without this the PIP renders the meeting
    // chrome over a fresh mic recording.
    set({
      previouslySelectedNoteType: noteType,
      audioSource: undefined,
      linkedEventId: linkedEventId ?? null,
      recordingNoteTemplateId: noteTemplateId ?? null,
      // Replaces any earlier draft's entry; promoted notes keep theirs.
      recordingSuggestedNoteTemplates: (() => {
        const {
          [draft.id]: _previousDraft,
          ...promoted
        } = get().recordingSuggestedNoteTemplates;
        return suggestedNoteTemplateIds?.length ? {
          ...promoted,
          [draft.id]: suggestedNoteTemplateIds
        } : promoted;
      })()
    });
    queryClient.setQueryData<NoteDetail>(noteQueryKeys.detail(draft.id), draft);
    if (noteType === NoteType.AI) {
      setRecordingId(draft.id);
    }
    const todayKey = clinicClock().today();
    const canViewNotes = hasPermission(currentUser, PERMISSIONS.CLINICAL_NOTES_READ);
    const bumpListFor = async (pid?: string) => {
      const params = {
        patientId: pid,
        createdDate: [todayKey, todayKey] as [string, string]
      };
      const listKey = noteQueryKeys.list(params);
      let cachedData = queryClient.getQueryData<Page<NoteListItem>>(listKey);
      try {
        // Only prefetch if the caller can read notes — otherwise the
        // backend will 403. Cache may still get optimistically updated
        // below if a prior successful fetch is in cache.
        if (!cachedData && canViewNotes) {
          await queryClient.prefetchQuery<Page<NoteListItem> | null>({
            queryKey: listKey,
            queryFn: () => getNotes(params)
          });
          cachedData = queryClient.getQueryData<Page<NoteListItem>>(listKey);
        }
      } finally {
        // just add the draft to the cache
      }
      const baseData = cachedData?.data ?? [];
      const localNoteIndex = baseData.findIndex(n => n.id === localId);
      const localNoteExists = localNoteIndex >= 0;

      // Convert NoteDetail to NoteListItem format
      const draftAsListItem: NoteListItem = {
        id: draft.id,
        summary: draft.summary,
        templateId: draft.template?.id,
        createdDate: draft.createdDate,
        visitDate: draft.createdDate,
        lastEditedDate: draft.createdDate,
        patient: notePatientToPatientSummary(draft.patient),
        providers: draft.providers,
        version: draft.version,
        aiGenerated: draft.aiGenerated,
        sourceDocuments: draft.sourceDocuments,
        hasAnswers: draft.answers && draft.answers.length > 0 || false
      };
      const newData = localNoteExists ? [...baseData.slice(0, localNoteIndex), draftAsListItem, ...baseData.slice(localNoteIndex + 1)] : [draftAsListItem, ...baseData];
      const baseTotal = cachedData?.total ?? baseData.length;
      const nextTotal = localNoteExists ? baseTotal : baseTotal + 1;
      queryClient.setQueryData<Page<NoteListItem>>(listKey, {
        data: newData,
        total: nextTotal
      });
    };
    bumpListFor(undefined);
    bumpListFor(currentPatient.id);
    return draft;
  },
  setDetections: updater => set(prev => ({
    detections: updater(prev.detections)
  })),
  // session slice
  isGenerateRequested: false,
  requestGenerateNote: () => {
    console.log(`${LOG_PREFIX()} Generate note requested: noteId=${get().currentRecordingId}`);
    posthog.capture("scribe_generate_requested", {
      note_id: get().currentRecordingId
    });
    set({
      isGenerateRequested: true
    });
  },
  clearGenerateRequest: () => set({
    isGenerateRequested: false
  }),
  onScribePage: false,
  visiblePatientId: undefined,
  setVisiblePatientId: id => set({
    visiblePatientId: id
  }),
  setRecordingSeconds: s => set({
    recordingSeconds: s
  })
}), {
  name: "scribe-store",
  partialize: state => {
    return {
      previouslySelectedNoteType: state.previouslySelectedNoteType,
      fillFromLastNote: state.fillFromLastNote,
      selectedDevice: state.selectedDevice
    };
  },
  version: 4,
  migrate: (persistedState: unknown, _version: number) => {
    type PersistedScribeState = {
      previouslySelectedNoteType: NoteType;
      fillFromLastNote?: boolean;
      selectedDevice: string | undefined;
    };
    const state = persistedState as PersistedScribeState;
    return state;
  }
}));

// Selector hooks (helpers to maintain separation of concern and avoid re-renders)
export const useScribeDevice = () => {
  return useScribeStore(useShallow(_temp));
};
export const useScribeTime = () => {
  const $ = _c(7);
  const time = useScribeStore(useShallow(_temp2));
  const {
    recordingNote
  } = useRecordingNote();
  const setRecordedMoreThan5Seconds = useScribeStore(_temp3);
  let t0;
  if ($[0] !== recordingNote || $[1] !== setRecordedMoreThan5Seconds || $[2] !== time) {
    t0 = () => {
      if (time.recordingSeconds >= 5) {
        setRecordedMoreThan5Seconds(true);
      }
      if (recordingNote?.duration && !time.recordingSeconds) {
        time.setRecordingSeconds(Math.floor(recordingNote.duration / 1000));
      }
    };
    $[0] = recordingNote;
    $[1] = setRecordedMoreThan5Seconds;
    $[2] = time;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const t1 = recordingNote?.duration;
  let t2;
  if ($[4] !== t1 || $[5] !== time.recordingSeconds) {
    t2 = [time.recordingSeconds, t1];
    $[4] = t1;
    $[5] = time.recordingSeconds;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useEffect(t0, t2);
  return time;
};
export const useScribeRecording = (): UseScribeRecording => {
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const recordingStore = useScribeStore(useShallow(s => ({
    audioStream: s.audioStream,
    mediaRecorder: s.mediaRecorder,
    isActive: s.isActive,
    isProcessing: s.isProcessing,
    error: s.error,
    chunkIntervalId: s.chunkIntervalId,
    secondsTimerId: s.secondsTimerId,
    startAudio: s.startAudio,
    pauseAudio: s.pauseAudio,
    stopAudio: s.stopAudio,
    switchAudioDevice: s.switchAudioDevice,
    setAudioDevice: s.setAudioDevice,
    currentRecordingId: s.currentRecordingId,
    audioSource: s.audioSource,
    recordingNoteState: s.recordingNoteState,
    setRecordingId: s.setRecordingId,
    setAudioSource: s.setAudioSource,
    setRecordingNoteState: s.setRecordingNoteState,
    setRecordedMoreThan5Seconds: s.setRecordedMoreThan5Seconds,
    recordedMoreThan5Seconds: s.recordedMoreThan5Seconds,
    onScribePage: s.onScribePage,
    selectedDevice: s.selectedDevice,
    visiblePatientId: s.visiblePatientId,
    setVisiblePatientId: s.setVisiblePatientId,
    isTranscribing: s.isTranscribing,
    transcriptionErrorNoteId: s.transcriptionErrorNoteId,
    isStartingRecording: s.isStartingRecording,
    setIsTranscribing: s.setIsTranscribing,
    setTranscriptionErrorNoteId: s.setTranscriptionErrorNoteId,
    setIsStartingRecording: s.setIsStartingRecording,
    setRecordingSeconds: s.setRecordingSeconds
  })));
  const subDomain = useTenantStore(s_0 => s_0.currentTenant);
  const noteState = useScribeStore(useShallow(s_1 => s_1.noteState));
  const linkedEventId = useScribeStore(s_2 => s_2.linkedEventId);
  const recordingNoteTemplateId = useScribeStore(s_3 => s_3.recordingNoteTemplateId);
  const {
    setCurrentNoteState
  } = useScribeNote();
  const {
    currentUser,
    currentLocationId
  } = useUser();
  const mimeType = getSupportedMimeType();
  const userHasAiScribeUsePermission = useMemo(() => {
    return hasPermission(currentUser, PERMISSIONS.AI_SCRIBE_USE);
  }, [currentUser]);
  const pathname = usePathname();
  const onScribePage = useMemo(() => pathname?.includes("/scribe/playground"), [pathname]);
  const transcriptionDeps: ScribeTranscriptionDeps = {
    queryClient,
    currentUserId: currentUser?.id,
    currentLocationId: currentLocationId ?? undefined,
    selectedNoteId: recordingStore.currentRecordingId,
    startAudio: recordingStore.startAudio,
    acquireMic: useScribeStore.getState().acquireMic,
    currentRecordingId: recordingStore.currentRecordingId,
    setRecordingId: recordingStore.setRecordingId,
    promoteDraftRecording: useScribeStore.getState().promoteDraftRecording,
    onScribePage,
    subDomain: subDomain ?? "",
    navigateWithProgress,
    linkedEventId,
    recordingNoteTemplateId
  };
  useEffect(() => {
    console.log(`${LOG_PREFIX()} Recording ID changed: ${recordingStore.currentRecordingId}`);
    posthog.capture("scribe_current_recording_id_changed", {
      current_recording_id: recordingStore.currentRecordingId,
      recording_note_state: recordingStore.recordingNoteState,
      is_active: recordingStore.isActive,
      note_state: noteState
    });
  }, [recordingStore.currentRecordingId]);
  const isActive = useMemo(() => {
    return recordingStore.recordingNoteState === CreateNoteState.RECORDING || recordingStore.recordingNoteState === CreateNoteState.PAUSED;
  }, [recordingStore.recordingNoteState]);
  const handlePlayPause = useCallback(async (patient?: HandlePlayPausePatient, overrideOptOut?: boolean): Promise<boolean> => {
    const patientId = patient?.id;
    const aiScribeConsent = patient?.aiScribeConsent;
    try {
      if (!currentUser?.id) {
        throw new Error("Unable to authenticate user. Please try again later.");
      }
      if (recordingStore.recordingNoteState === CreateNoteState.RECORDING) {
        console.log(`${LOG_PREFIX()} Pausing recording: noteId=${recordingStore.currentRecordingId}`);
        recordingStore.pauseAudio();
        posthog.capture("scribe_paused", {
          note_id: recordingStore.currentRecordingId,
          patient_id: patientId,
          provider_id: currentUser.id
        });
        return false;
      }

      // Telehealth resume bypasses the mic-only transcription bootstrap.
      if (recordingStore.audioSource === "telehealth") {
        try {
          await recordingStore.startAudio({});
        } catch {
          // startAudio already surfaced a specific "Failed to resume visit"
          // toast and rolled back its state — swallow here so the generic
          // outer catch doesn't stack a second toast.
          return false;
        }
        posthog.capture("telehealth_visit_resumed", {
          note_id: recordingStore.currentRecordingId,
          provider_id: currentUser.id
        });
        return true;
      }
      recordingStore.setIsStartingRecording(true);
      const previousState = recordingStore.recordingNoteState;
      recordingStore.setRecordingNoteState(CreateNoteState.RECORDING);
      try {
        await startRecordingWithTranscriptionFn({
          deviceId: recordingStore.selectedDevice,
          mimeType,
          chunkMs: 30000,
          resetTimer: false
        }, {
          ...transcriptionDeps,
          patientId,
          patientAiScribeConsent: aiScribeConsent,
          overrideOptOut
        });
        console.log(`${LOG_PREFIX()} Recording started: noteId=${recordingStore.currentRecordingId}`);
        posthog.capture("scribe_started", {
          note_id: recordingStore.currentRecordingId,
          patient_id: patientId,
          provider_id: currentUser.id
        });
        return true;
      } catch (error_0) {
        // Reset state if recording failed to start
        recordingStore.setRecordingNoteState(previousState);
        throw error_0;
      } finally {
        recordingStore.setIsStartingRecording(false);
      }
    } catch (error) {
      const errorMessage = await parseError(error);
      console.error(`${LOG_PREFIX()} Start recording error: ${errorMessage}`, error);
      posthog.capture("scribe_start_error", {
        error: errorMessage,
        note_id: recordingStore.currentRecordingId
      });
      // The mic was refused and MicAccessDialog is open with steps for this
      // browser; a generic toast on top would only repeat it less usefully.
      // Keyed on the dialog, not the error's name: AbortError and the like
      // also come from fetches, and those still need the toast.
      if (useScribeStore.getState().micErrorKind) return false;
      toast({
        title: "Error",
        description: errorMessage,
        variant: "error"
      });
      return false;
    }
  }, [currentUser, mimeType, recordingStore, transcriptionDeps]);
  const handleResumeRecording = useCallback(async (note: NoteListItem) => {
    if (!userHasAiScribeUsePermission) {
      toast({
        title: "Error",
        description: "You do not have permission to use the AI scribe. Please contact your administrator for more information.",
        variant: "error"
      });
      return;
    }
    if (!currentUser) {
      toast({
        title: "Error",
        description: "No user found.",
        variant: "error"
      });
      return;
    }
    if (!note?.patient) {
      toast({
        title: "Error",
        description: "No patient found.",
        variant: "error"
      });
      return;
    }
    if (!hasPermission(currentUser, PERMISSIONS.CLINICAL_NOTES_READ) && !hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE)) {
      toast({
        title: "Error",
        description: "You do not have permission to view notes. Please contact your administrator for more information.",
        variant: "error"
      });
      return;
    }
    posthog.startSessionRecording({
      sampling: true
    });
    setCurrentNoteState(CreateNoteState.PAUSED);
    recordingStore.setRecordingNoteState(CreateNoteState.PAUSED);
    const noteDetail = await queryClient.fetchQuery<NoteDetail | undefined>(noteQueryOptions.detail(note.id));
    if (!noteDetail) {
      toast({
        title: "Error",
        description: "Note not found.",
        variant: "error"
      });
      return;
    }
    // Resuming an existing note — any template carried over from the last
    // checklist-launched recording no longer applies.
    useScribeStore.getState().setRecordingNoteTemplateId(null);
    recordingStore.setRecordingId(note.id);
  }, [currentUser, userHasAiScribeUsePermission, setCurrentNoteState, recordingStore.setRecordingNoteState, recordingStore.setRecordingId]);
  const flushTranscription = useCallback(async (): Promise<string | undefined> => {
    const recorder = recordingStore.mediaRecorder;
    const hasActiveRecorder = recorder && recorder.state !== "inactive";
    const noteId = useScribeStore.getState().currentRecordingId;
    const stopPromise = hasActiveRecorder ? new Promise<void>(resolve => {
      // here's how this works:
      // this event listener is added to the bottom of the listener queue, meaning it is executed after all the other listeners are executed
      // this means that waiting this promise will absolutely guarantee that all the other listeners are executed, i.e. the chunk is enqueued
      recorder.addEventListener("stop", () => {
        resolve();
      });
      recorder.stop();
    }) : Promise.resolve();

    // IMPORTANT: Wait for onstop to fire BEFORE calling stopAudio.
    // The onstop handler reads currentRecordingId from state to enqueue the final chunk.
    // If we call stopAudio before onstop fires, it clears currentRecordingId and the
    // final chunk is skipped (the "Missing noteId for enqueue" warning).
    await stopPromise;

    // Only stop audio if there was an active recorder to avoid clearing
    // currentRecordingId when no recording is in progress
    if (hasActiveRecorder) {
      recordingStore.stopAudio(currentUser);
    }

    // Wait for the queue to finish processing all pending chunks for this note
    const maxWaitMs = 5 * 60 * 1000; // 5 minute max wait
    const pollIntervalMs = 500;
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitMs) {
      const queueState = useTranscriptionQueue.getState();
      const scribeState = useScribeStore.getState();
      const noteItems = noteId ? queueState.getItemsForNote(noteId) : [];

      // Check if any items are still pending or processing
      const hasPendingItems = noteItems.some(item => item.status === "pending" || item.status === "processing");

      // Also check scribeState.isProcessing which is true while the onstop handler
      // is still enqueuing the final chunk (before it appears in the queue)
      if (!hasPendingItems && !queueState.isProcessing && !scribeState.isProcessing) {
        // All done!
        const completedCount = noteItems.filter(item_0 => item_0.status === "completed").length;
        const failedCount = noteItems.filter(item_1 => item_1.status === "failed" || item_1.status === "permanent_failure").length;
        posthog.capture("transcription_flushed", {
          patient_id: recordingStore.visiblePatientId,
          provider_id: currentUser?.id,
          note_id: noteId,
          total_chunks: noteItems.length,
          completed_count: completedCount,
          failed_count: failedCount,
          wait_duration_ms: Date.now() - startTime
        });

        // Clean up completed items now that we've reported them
        if (noteId) {
          queueState.clearCompletedForNote(noteId);
        }
        return noteId;
      }

      // Wait before polling again
      await new Promise(resolve_0 => setTimeout(resolve_0, pollIntervalMs));
    }

    // Timeout reached - return whatever we have
    console.warn(`${LOG_PREFIX()} flushTranscription: Timeout waiting for queue to complete`);
    const finalQueueState = useTranscriptionQueue.getState();
    const finalNoteItems = noteId ? finalQueueState.getItemsForNote(noteId) : [];
    const pendingCount = finalNoteItems.filter(item_2 => item_2.status === "pending" || item_2.status === "processing").length;
    posthog.capture("transcription_flush_timeout", {
      pending_count: pendingCount,
      total_note_items: finalNoteItems.length,
      patient_id: recordingStore.visiblePatientId,
      provider_id: currentUser?.id,
      note_id: noteId,
      wait_duration_ms: maxWaitMs
    });
    return noteId;
  }, [recordingStore, currentUser]);
  return {
    ...recordingStore,
    isActive,
    currentRecordingId: recordingStore.currentRecordingId,
    recordingNoteState: recordingStore.recordingNoteState ?? noteState,
    onScribePage,
    isProcessing: recordingStore.isProcessing,
    handlePlayPause,
    flushTranscription,
    handleResumeRecording,
    userHasAiScribeUsePermission
  };
};
export const useScribeNote = () => {
  return useScribeStore(useShallow(_temp4));
};
export const useScribeSession = () => {
  return useScribeStore(useShallow(_temp5));
};
function _temp(s) {
  return {
    selectedDevice: s.selectedDevice,
    setSelectedDevice: s.setSelectedDevice,
    audioDevices: s.audioDevices,
    setAudioDevices: s.setAudioDevices,
    micPermissionGranted: s.micPermissionGranted,
    setMicPermissionGranted: s.setMicPermissionGranted
  };
}
function _temp2(s) {
  return {
    recordingSeconds: s.recordingSeconds,
    setRecordingSeconds: s.setRecordingSeconds
  };
}
function _temp3(s_0) {
  return s_0.setRecordedMoreThan5Seconds;
}
function _temp4(s) {
  return {
    previouslySelectedNoteType: s.previouslySelectedNoteType,
    fillFromLastNote: s.fillFromLastNote,
    setFillFromLastNote: s.setFillFromLastNote,
    setPreviouslySelectedNoteType: s.setPreviouslySelectedNoteType,
    defaultNoteTemplateId: s.defaultNoteTemplateId,
    setDefaultNoteTemplateId: s.setDefaultNoteTemplateId,
    recordingNoteTemplateId: s.recordingNoteTemplateId,
    recordingSuggestedNoteTemplates: s.recordingSuggestedNoteTemplates,
    setRecordingNoteTemplateId: s.setRecordingNoteTemplateId,
    noteState: s.noteState,
    setNoteState: s.setNoteState,
    detections: s.detections,
    setDetections: s.setDetections,
    handleNewNote: s.handleNewNote,
    setCurrentNoteState: s.setNoteState
  };
}
function _temp5(s) {
  return {
    isGenerateRequested: s.isGenerateRequested,
    requestGenerateNote: s.requestGenerateNote,
    clearGenerateRequest: s.clearGenerateRequest
  };
}